// src/utils/apiUtils.js
import { API_BASE } from "@/constants/api"; // Make sure API_BASE is imported

/**
 * Builds the correct API URL for local proxy or live environment.
 * @param {string} endpointPath - The API endpoint path (e.g., 'vdb/brands/dropdown').
 * @param {URLSearchParams|Object|null} params - Query parameters.
 * @returns {string} The fully constructed API URL.
 */
export function buildApiUrl(endpointPath, params = null) {
  let url;
  // Check if API_BASE indicates the local proxy script is being used
  const isLocalProxy = API_BASE.includes('local_vdb_api.php');

  if (isLocalProxy) {
    // Local Proxy: Append endpoint path, then use '&' for subsequent params
    url = `${API_BASE}${endpointPath}`;
    if (params) {
      const queryParams = (params instanceof URLSearchParams) ? params : new URLSearchParams(params);
      const queryString = queryParams.toString();
      if (queryString) {
         url += `&${queryString}`; // Use '&'
      }
    }
  } else {
    // Live/Direct API: Append endpoint path, then use '?' for params
    url = `${API_BASE}${endpointPath}`;
    if (params) {
      const queryParams = (params instanceof URLSearchParams) ? params : new URLSearchParams(params);
      const queryString = queryParams.toString();
       if (queryString) {
         url += `?${queryString}`; // Use '?'
       }
    }
  }
  console.log(`Built API URL: ${url}`); // Keep logging for debugging
  return url;
}

/**
 * A wrapper around fetch that automatically adds the Authorization header.
 * @param {string} endpointPath - The API endpoint path (e.g., 'vdb/brands/dropdown').
 * @param {URLSearchParams|Object|null} params - Query parameters.
 * @param {RequestInit} options - Additional fetch options.
 * @returns {Promise<any>} - The JSON response from the API.
 */
export async function fetchWithAuth(endpointPath, params = null, options = {}) {
    const url = buildApiUrl(endpointPath, params);
    const apiKey = window.ercData?.api_key || null;

    const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
    };

    if (apiKey) {
        headers['Authorization'] = `Bearer ${apiKey}`;
    }

    try {
        const response = await fetch(url, { ...options, headers });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Invalid JSON response from server' }));
            console.error(`API Error on ${url}: ${response.status}`, errorData);
            throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error(`Fetch failed for ${endpointPath}:`, error);
        throw error; // Re-throw the error to be caught by the calling function
    }
}
